import { createContext, useEffect, useState } from 'react';
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion';
import type { AppProps } from 'next/app';
import { Josefin_Sans } from 'next/font/google';
import localFont from 'next/font/local';
import { useRouter } from 'next/router';
import Script from 'next/script';
import type { UserConfig } from 'next-i18next';
import { appWithTranslation } from 'next-i18next';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '../common/styles/global.css';

import nextI18NextConfig from '../../next-i18next.config';

const modernist = localFont({
  src: [
    { path: '../assets/font/Sk-Modernist-Mono.otf', weight: '100' },
    { path: '../assets/font/Sk-Modernist-Regular.otf', weight: '400' },
    { path: '../assets/font/Sk-Modernist-Bold.otf', weight: '600' },
  ],
  variable: '--font-modernist',
});

const josefin = Josefin_Sans({ subsets: ['latin'], preload: false });

const GTM_ID = 'GTM-W2JJX9CC';
const LINKEDIN_PARTNER_ID = '10716937';

export const ConsentContext = createContext({
  enabled: false,
  handleConsent: (_value: boolean) => {},
});

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [consentEnabled, setConsentEnabled] = useState(false);

  const handleConsent = (value: boolean) => {
    setConsentEnabled(value);
  };

  const gtmScriptEnabled =
    typeof window !== 'undefined' &&
    !window.location?.hostname?.includes('staging') &&
    router.query['cookie-consent'] !== 'false';

  useEffect(() => {
    if (consentEnabled && gtmScriptEnabled) {
      const handleRouteChange = (url: string) => {
        if (typeof window === 'undefined') {
          return;
        }
        const win = window as Window & {
          dataLayer?: Array<Record<string, unknown>> & {
            consentStatus?: { marketing?: boolean };
          };
        };

        if (win.dataLayer) {
          win.dataLayer.push({
            event: 'pageview',
            page: url,
            consentGranted: true,
            marketingConsent: win.dataLayer.consentStatus?.marketing || false,
          });
        }
      };
      router.events.on('routeChangeComplete', handleRouteChange);
      handleRouteChange(window.location.pathname);
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    }
  }, [consentEnabled, gtmScriptEnabled, router.events]);

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence mode="sync" initial={false}>
        <ConsentContext.Provider value={{ enabled: consentEnabled, handleConsent }}>
          <style jsx global>{`
            body {
              font-family: ${modernist.style.fontFamily}, sans-serif;
              font-size: 21px;
              line-height: 30px;
            }
            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
              font-family: ${modernist.style.fontFamily}, sans-serif;
            }
            .josefin {
              font-family: ${josefin.style.fontFamily}, sans-serif;
            }
          `}</style>
          {gtmScriptEnabled && consentEnabled && (
            <>
              <Script id="gtm-script" type="module">
                {`
                (function(w,d,s,l,i){w[l] = w[l] || [];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${GTM_ID}');
                `}
              </Script>
              <Script id="linkedin-insight-tag" strategy="afterInteractive">
                {`
                _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
                window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
                window._linkedin_data_partner_ids.push(_linkedin_partner_id);
                (function(l) {
                  if (!l) {
                    window.lintrk = function(a, b) {
                      window.lintrk.q.push([a, b]);
                    };
                    window.lintrk.q = [];
                  }
                  var s = document.getElementsByTagName("script")[0];
                  var b = document.createElement("script");
                  b.type = "text/javascript";
                  b.async = true;
                  b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                  s.parentNode.insertBefore(b, s);
                })(window.lintrk);
                `}
              </Script>
            </>
          )}

          <m.div
            key={router.asPath}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}>
            <Component {...pageProps} />
          </m.div>
        </ConsentContext.Provider>
      </AnimatePresence>
    </LazyMotion>
  );
}

export default appWithTranslation(App, nextI18NextConfig as UserConfig);
